import { useEffect, useState } from "react";
import { MdAdd, MdEdit, MdDelete, MdSearch, MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Card from "components/card";
import { Patient } from "types/patient";
import PatientForm from "./PatientForm";
import { patientService } from "services/patientService";
import { AdminOnly } from "components/auth/AdminOnly";
import ConfirmationModal from "components/modal/ConfirmationModal";

const Patients = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    patient: Patient | null;
    isLoading: boolean;
  }>({
    isOpen: false,
    patient: null,
    isLoading: false,
  });

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await patientService.getPatients();
      if (response.success) {
        setPatients(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Failed to fetch patients");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  const handleAddSuccess = () => {
    setIsFormOpen(false);
    setSelectedPatient(null);
    fetchPatients();
  };

  const handleEdit = (patient: Patient) => {
    setSelectedPatient(patient);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (patient: Patient) => {
    setDeleteConfirmation({
      isOpen: true,
      patient,
      isLoading: false,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.patient) return;

    try {
      setDeleteConfirmation(prev => ({ ...prev, isLoading: true }));
      const response = await patientService.deletePatient(deleteConfirmation.patient.id);
      
      if (response.success) {
        setPatients(prev => 
          prev.filter(p => p.id !== deleteConfirmation.patient?.id)
        );
        setDeleteConfirmation({
          isOpen: false,
          patient: null,
          isLoading: false,
        });
      } else {
        setError(response.message || "Failed to delete patient");
      }
    } catch (err) {
      setError("Failed to delete patient");
      console.error(err);
    } finally {
      setDeleteConfirmation(prev => ({ ...prev, isLoading: false }));
    }
  };

  const toggleRow = (patientId: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(patientId)) {
        newSet.delete(patientId);
      } else {
        newSet.add(patientId);
      }
      return newSet;
    });
  };

  const handleViewPatient = (patientId: number) => {
    navigate(`/admin/patients/${patientId}`);
  };

  const filteredPatients = patients.filter((patient) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      patient.name.toLowerCase().includes(searchLower) ||
      patient.email.toLowerCase().includes(searchLower) ||
      patient.patient_profile.phone.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg font-semibold text-navy-700 dark:text-white">
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg font-semibold text-red-500">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 grid h-full">
      <div className="col-span-12 h-fit w-full xl:col-span-12 2xl:col-span-12">
        <Card extra={"w-full h-full px-6 pb-6 sm:overflow-x-auto"}>
          <div className="relative flex items-center justify-between pt-4">
            <div className="text-xl font-bold text-navy-700 dark:text-white">
              Pacientes
            </div>
            <button 
              onClick={() => setIsFormOpen(true)}
              className="flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
            >
              <MdAdd className="h-6 w-6" />
              <span>Agregar Paciente</span>
            </button>
          </div>

          <div className="mt-4 flex w-full max-w-md items-center gap-2 rounded-lg border border-gray-300 px-3 py-2">
            <MdSearch className="h-5 w-5 text-gray-500" />
            <input
              type="text"
              placeholder="Buscar por nombre, email o teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-transparent text-sm text-gray-900 placeholder-gray-500 focus:outline-none dark:text-white"
            />
          </div>
            
          <div className="mt-4">
            {/* Desktop Table */}
            <table className="hidden w-full md:table">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="min-w-[150px] py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Nombre</p>
                  </th>
                  <th className="min-w-[150px] py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Email</p>
                  </th>
                  <th className="min-w-[150px] py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Teléfono</p>
                  </th>
                  <th className="min-w-[150px] py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Fecha de Nacimiento</p>
                  </th>
                  <th className="min-w-[100px] py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Género</p>
                  </th>
                  <th className="min-w-[150px] py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Seguro</p>
                  </th>
                  <th className="py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Acciones</p>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredPatients.map((patient) => (
                  <tr key={patient.id} className="border-b border-gray-200">
                    <td className="py-3 pr-4">
                      <p 
                        className="cursor-pointer text-sm font-medium text-navy-700 hover:text-brand-500 dark:text-white dark:hover:text-brand-400"
                        onClick={() => handleViewPatient(patient.id)}
                      >
                        {patient.name}
                      </p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm font-medium text-gray-600">
                        {patient.email}
                      </p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm font-medium text-gray-600">
                        {patient.patient_profile.phone}
                      </p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm font-medium text-gray-600">
                        {new Date(patient.patient_profile.birth_date).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm font-medium text-gray-600">
                        {patient.patient_profile.gender === 'male' ? 'Masculino' :
                         patient.patient_profile.gender === 'female' ? 'Femenino' : 'Otro'}
                      </p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm font-medium text-gray-600">
                        {patient.patient_profile.health_insurance_company}
                      </p>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <button
                          className="rounded-lg p-2 text-brand-500 hover:bg-gray-100"
                          title="Editar"
                          onClick={() => handleEdit(patient)}
                        >
                          <MdEdit className="h-5 w-5" />
                        </button>
                        <button
                          className="rounded-lg p-2 text-red-500 hover:bg-gray-100"
                          title="Eliminar"
                          onClick={() => handleDeleteClick(patient)}
                        >
                          <MdDelete className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile List */}
            <div className="md:hidden">
              {filteredPatients.map((patient) => (
                <div key={patient.id} className="mb-4 overflow-hidden rounded-lg border border-gray-200">
                  <div 
                    className="flex cursor-pointer items-center justify-between bg-white p-4 dark:bg-navy-700"
                    onClick={() => toggleRow(patient.id)}
                  >
                    <div>
                      <p 
                        className="cursor-pointer font-medium text-navy-700 hover:text-brand-500 dark:text-white dark:hover:text-brand-400"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewPatient(patient.id);
                        }}
                      >
                        {patient.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        {patient.email}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="rounded-lg p-2 text-brand-500 hover:bg-gray-100"
                        title="Editar"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(patient);
                        }}
                      >
                        <MdEdit className="h-5 w-5" />
                      </button>
                      <button
                        className="rounded-lg p-2 text-red-500 hover:bg-gray-100"
                        title="Eliminar"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(patient);
                        }}
                      >
                        <MdDelete className="h-5 w-5" />
                      </button>
                      {expandedRows.has(patient.id) ? (
                        <MdKeyboardArrowUp className="h-6 w-6 text-gray-500" />
                      ) : (
                        <MdKeyboardArrowDown className="h-6 w-6 text-gray-500" />
                      )}
                    </div>
                  </div>
                  
                  {expandedRows.has(patient.id) && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4 dark:bg-navy-800">
                      <div className="grid gap-2">
                        <div>
                          <p className="text-xs font-semibold text-gray-500">Teléfono</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300">{patient.patient_profile.phone}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500">Fecha de Nacimiento</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {new Date(patient.patient_profile.birth_date).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500">Género</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {patient.patient_profile.gender === 'male' ? 'Masculino' :
                             patient.patient_profile.gender === 'female' ? 'Femenino' : 'Otro'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500">Seguro</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {patient.patient_profile.health_insurance_company}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {isFormOpen && (
        <PatientForm
          onClose={() => {
            setIsFormOpen(false);
            setSelectedPatient(null);
          }}
          onSuccess={handleAddSuccess}
          patient={selectedPatient}
        />
      )}

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({
          isOpen: false,
          patient: null,
          isLoading: false,
        })}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Paciente"
        message={`¿Estás seguro que deseas eliminar al paciente ${deleteConfirmation.patient?.name}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        isLoading={deleteConfirmation.isLoading}
      />
    </div>
  );
};

export default Patients; 